import { defineComponent as _defineComponent } from 'vue';
import { useSlots, ref, computed, reactive, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import EnIcon from 'Components/icons/EnIcon.vue';
import EnClearSortIcon from 'Components/icons/EnClearSortIcon.vue';
import EnCheckboxButton from 'Components/inputs/EnCheckbox/EnCheckbox.vue';
import EnButton from 'Components/base/EnButton/EnButton.vue';
import EnDraggableWrapper from 'Components/base/EnDraggableWrapper/EnDraggableWrapper.vue';
import { EnMenu, EnMenuItem, MenuSeparator } from 'Components/base/EnMenu';
import { ArrowUp, ArrowDown, ListFilter, EyeOff, Plus } from 'lucide-vue-next';
import { useAutoCloseOnScroll } from 'Js/utils/useAutoCloseOnScroll.js';
import { useIdBuilder } from '../composables/useIdBuilder';
import { ColumnSortDirections } from '../types/columnSortDirection';
export default /*@__PURE__*/ _defineComponent({
    __name: 'EnDatagridHeaderList',
    props: {
        columns: (Array),
        sortBy: (Array),
        allSelected: Boolean,
        dense: Boolean,
        allowAdding: Boolean,
        someSelected: Boolean,
        getSortIcon: Function,
        addItem: Function,
        isSorted: Function,
        selectAll: Function,
        toggleSort: Function,
        dataGridKey: [String, (Array)],
        toggleFilterVisibility: Function,
        tableHoveredColumnKey: [String, null],
        hasDynamicColumns: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        variant: {
            type: String,
            default: ''
        }
    },
    emits: ['update:sort', 'update:sort-by', 'all-items-selected', 'columns-reordered', 'remove'],
    setup(__props, { expose: __expose, emit: __emit }) {
        __expose();
        const { t } = useI18n();
        const props = __props;
        const { buildId } = useIdBuilder();
        const menuComponents = ref({});
        const emit = __emit;
        const slots = useSlots();
        const localColumns = ref(props.columns);
        const menuVisibility = reactive({});
        // Function to ensure column key exists in menu visibility
        const ensureMenuKey = (columnKey) => {
            if (!(columnKey in menuVisibility)) {
                menuVisibility[columnKey] = false;
            }
        };
        // Function to hide all menus
        const hideAllMenus = () => {
            Object.keys(menuVisibility).forEach(key => {
                menuVisibility[key] = false;
            });
        };
        const buildAddId = computed(() => {
            return buildId('slot-headers-button', props.dataGridKey);
        });
        // Function to handle menu visibility updates
        const handleMenuVisibilityUpdate = (columnKey, isOpen) => {
            ensureMenuKey(columnKey);
            if (isOpen) {
                // Hide all other menus when opening this one
                Object.keys(menuVisibility).forEach(key => {
                    if (key !== columnKey) {
                        menuVisibility[key] = false;
                    }
                });
            }
            menuVisibility[columnKey] = isOpen;
        };
        // Use the auto-close composable with 0 threshold for immediate closure
        const { updateMenuState } = useAutoCloseOnScroll({
            scrollThreshold: 0,
            onClose: hideAllMenus
        });
        // Track if any menu is open
        const hasOpenMenu = computed(() => {
            return Object.values(menuVisibility).some(isOpen => isOpen);
        });
        // Watch for menu state changes and update the composable
        watch(hasOpenMenu, (isOpen) => {
            updateMenuState(isOpen);
        }, { immediate: true });
        const allItemsSelected = computed({
            get() {
                return props.allSelected;
            },
            set(value) {
                emit('all-items-selected', value);
            }
        });
        function isColumnSorted(column) {
            return props.sortBy?.some(sortedColumn => sortedColumn.key === column.key);
        }
        function getSortDirection(column) {
            const sortedColumn = props.sortBy?.find(sortedColumn => sortedColumn.key === column.key);
            if (sortedColumn) {
                return sortedColumn.order;
            }
        }
        const sortAsc = (column) => {
            emit('update:sort-by', [{ key: column.key, order: ColumnSortDirections.Ascending }]);
        };
        const sortDesc = (column) => {
            emit('update:sort-by', [{ key: column.key, order: ColumnSortDirections.Descending }]);
        };
        const resetSort = () => {
            emit('update:sort-by', []);
        };
        // The header list only holds the columns on display, so more than one means one of them can go.
        const columnsCanBeHidden = computed(() => (props.columns?.filter(column => column.canBeToggled).length ?? 0) > 1);
        function columnCanBeDeleted(column) {
            if (!columnsCanBeHidden.value)
                return false;
            return props.hasDynamicColumns && !columnIsCheckbox(column) && !column.isFixed && column.canBeToggled;
        }
        function columnCanBeFiltered(column) {
            return column.canBeFiltered;
        }
        function columnHasSortMenu(column) {
            return column.sortable || isColumnSorted(column);
        }
        function columnHasMenu(column) {
            return columnHasSortMenu(column) || columnCanBeDeleted(column) || columnCanBeFiltered(column);
        }
        function columnCanBeSorted(column) {
            return column.sortable && !props.isSorted?.(column);
        }
        function columnIsCheckbox(column) {
            return column.value === 'data-table-select';
        }
        function columnIsSingleSelect(column) {
            return column.value === 'data-table-single-select';
        }
        function columnIsSelection(column) {
            return columnIsCheckbox(column) || columnIsSingleSelect(column);
        }
        function columnIsExpand(column) {
            return column.value === 'data-table-expand';
        }
        function columnIsActions(column) {
            return column.value === 'actions';
        }
        function columnIsStatus(column) {
            return column.title === '' && column.isStatusCell;
        }
        function columnIsEmpty(column) {
            return column.title === '';
        }
        function columnIsStatic(column) {
            return columnIsSelection(column) || column?.isFixed === true;
        }
        function slotName(column) {
            return 'header.' + column.value;
        }
        function remove(key) {
            emit('remove', key);
        }
        function getHeaderClass(column) {
            return [column?.headerProps?.class,
                (columnIsStatic(column) ? 'static-column-header' : 'draggable-column-header'),
                (columnIsSelection(column) ? 'select-all-header pl-4.5' : ''),
                (columnIsExpand(column) ? 'expand-col-header' : ''),
                (columnIsActions(column) ? 'action-header fixed-header-col' : ''),
                (columnIsEmpty(column) ? 'column-empty-label' : ''),
                (columnIsStatus(column) ? 'pl-0' : ''),
            ].join(' ');
        }
        function hasSlot(column) {
            return Object.keys(slots).includes(slotName(column));
        }
        function setComponentRef(el, key) {
            if (el) {
                menuComponents.value[key] = el;
            }
            else {
                delete menuComponents.value[key];
            }
        }
        function closeMenu(key) {
            menuComponents.value[key].setOpen(false);
        }
        const isDragging = ref(false);
        const headerRow = ref(null);
        const hoveredColumnKey = ref(null);
        const onDragStart = () => {
            isDragging.value = true;
        };
        const onDragEnd = () => {
            isDragging.value = false;
        };
        const onDragUpdate = (e) => {
            const newOrder = [...localColumns.value];
            const wasReordered = e.oldIndex !== e.newIndex;
            if (!wasReordered)
                return;
            emit('columns-reordered', { newIndex: e.newIndex, oldIndex: e.oldIndex });
        };
        function onTableMouseOver(e) {
            const td = e.target.closest('.v-data-table__td:not(.v-data-table__th)');
            if (!td) {
                hoveredColumnKey.value = props.tableHoveredColumnKey ?? null;
                return;
            }
            const tr = td.parentElement;
            const colIndex = [...tr.children].indexOf(td);
            hoveredColumnKey.value = props.columns?.[colIndex]?.key ?? null;
        }
        function onTableMouseLeave() {
            hoveredColumnKey.value = null;
        }
        onMounted(() => {
            const el = headerRow.value?.$el ?? headerRow.value;
            const table = el?.closest('table');
            if (table) {
                table.addEventListener('mouseover', onTableMouseOver);
                table.addEventListener('mouseleave', onTableMouseLeave);
            }
        });
        onUnmounted(() => {
            const el = headerRow.value?.$el ?? headerRow.value;
            const table = el?.closest('table');
            if (table) {
                table.removeEventListener('mouseover', onTableMouseOver);
                table.removeEventListener('mouseleave', onTableMouseLeave);
            }
        });
        const __returned__ = { t, props, buildId, menuComponents, emit, slots, localColumns, menuVisibility, ensureMenuKey, hideAllMenus, buildAddId, handleMenuVisibilityUpdate, updateMenuState, hasOpenMenu, allItemsSelected, isColumnSorted, getSortDirection, sortAsc, sortDesc, resetSort, columnsCanBeHidden, columnCanBeDeleted, columnCanBeFiltered, columnHasSortMenu, columnHasMenu, columnCanBeSorted, columnIsCheckbox, columnIsSingleSelect, columnIsSelection, columnIsExpand, columnIsActions, columnIsStatus, columnIsEmpty, columnIsStatic, slotName, remove, getHeaderClass, hasSlot, setComponentRef, closeMenu, isDragging, headerRow, hoveredColumnKey, onDragStart, onDragEnd, onDragUpdate, onTableMouseOver, onTableMouseLeave, EnIcon, EnClearSortIcon, EnCheckboxButton, EnButton, EnDraggableWrapper, get EnMenu() { return EnMenu; }, get EnMenuItem() { return EnMenuItem; }, get MenuSeparator() { return MenuSeparator; }, get ArrowUp() { return ArrowUp; }, get ArrowDown() { return ArrowDown; }, get ListFilter() { return ListFilter; }, get EyeOff() { return EyeOff; }, get Plus() { return Plus; }, get ColumnSortDirections() { return ColumnSortDirections; } };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
